import { useMemo } from 'react';
import type {
  CustomerSplit,
  DealIncrementality,
  OpeningHoursDay,
  PlatformKey,
  StoreAnalytics,
  StoreResponse,
} from '@clarity/contract';
import {
  useCustomerSplit,
  useDealIncrementality,
  useStoreAnalytics,
} from '../../queries/index.js';
import { num } from '../format.js';
import { StoreAnalyticsEntry } from '../StoreAnalyticsEntry.js';
import { MythingsManualEntries } from '../MythingsManualEntries.js';

/**
 * BranchInsights (clarity-owner-analytics) — the FULL operational picture for ONE
 * branch, the Insights surface's single-branch view. Everything the owner-
 * analytics pipeline captures but the dashboard only hints at: ratings, delivery
 * & prep time, availability / reliability, the storefront conversion funnel,
 * opening hours, the new-vs-returning split, and per-deal incrementality — plus
 * the owner-entry form (what Clarity can't pull) and, for a MyThings branch, the
 * payouts/costs panel.
 *
 * All reads are cheap tenant-scoped repo reads (shared query cache with the
 * dashboard glance); null metrics render as an honest "not recorded yet", never
 * a fabricated zero.
 */

const PLATFORM_LABEL: Record<PlatformKey, string> = {
  talabat: 'Talabat',
  careem: 'Careem',
  mythings: 'MyThings',
};

const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/** This calendar month `[from, to)` + a human label (matches the entry form). */
function thisMonth(): { from: string; to: string; label: string } {
  const now = new Date();
  const from = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1)).toISOString();
  return {
    from,
    to: now.toISOString(),
    label: new Date(from).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' }),
  };
}

function hhmm(min: number): string {
  const h = Math.floor(min / 60) % 24;
  const m = min % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

function openingLine(day: OpeningHoursDay | undefined): string {
  if (!day || day.windows.length === 0) return 'closed';
  return day.windows.map((w) => `${hhmm(w.open)}\u2013${hhmm(w.close)}`).join(', ');
}

function conversionRate(a: StoreAnalytics | null): number | null {
  if (!a) return null;
  const visits = a.menuVisits ?? a.impressions;
  if (!visits || visits <= 0 || a.conversions == null) return null;
  return (a.conversions / visits) * 100;
}

export function BranchInsights({ store }: { store: StoreResponse }) {
  const win = useMemo(() => thisMonth(), []);
  const analytics = useStoreAnalytics(store.id);
  const split = useCustomerSplit(store.id, win.from, win.to);
  const incr = useDealIncrementality(store.id, win.from, win.to);

  const records = analytics.data?.status === 200 ? analytics.data.body : [];
  const a =
    records.find((r) => r.periodEnd >= win.from && r.periodStart < win.to) ?? records[0] ?? null;
  const splitBody: CustomerSplit | null = split.data?.status === 200 ? split.data.body : null;
  const deals: DealIncrementality[] = incr.data?.status === 200 ? incr.data.body : [];

  const platform = PLATFORM_LABEL[store.platformKey as PlatformKey];
  const delivery = a?.avgDeliveryMinutes ?? a?.avgPrepMinutes ?? null;
  const deliveryIsPrep = a?.avgDeliveryMinutes == null && a?.avgPrepMinutes != null;
  const conv = conversionRate(a);
  const openingHours = a?.openingHours ?? [];

  return (
    <div className="insights">
      {/* ── Operational metrics (availability + ratings live here; the
          #availability / #ratings notification deep-links scroll to it) ── */}
      <section
        id="availability"
        className="card insights-block"
        aria-label={`Operational metrics — ${store.name ?? platform}`}
      >
        <span id="ratings" aria-hidden="true" />
        <header className="insights-block__head">
          <h3>
            {store.name ?? platform}
            <span className="insights-block__chan">{platform}</span>
          </h3>
          <p className="muted">{win.label} · the operational picture for this branch.</p>
        </header>
        <div className="insights-stats">
          <Metric label="Rating" value={a?.ratingAvg != null ? `\u2605 ${a.ratingAvg.toFixed(1)}` : '—'} sub={a?.ratingCount != null ? `${num(a.ratingCount)} ratings` : 'not recorded yet'} />
          <Metric label={deliveryIsPrep ? 'Avg prep time' : 'Avg delivery time'} value={delivery != null ? `${Math.round(delivery)} min` : '—'} sub={delivery != null ? (deliveryIsPrep ? 'kitchen prep' : 'order to delivered') : 'not recorded yet'} />
          <Metric label="Availability" value={a?.availabilityPct != null ? `${a.availabilityPct.toFixed(1)}%` : '—'} sub={a?.offlineMinutes != null ? `${num(Math.round(a.offlineMinutes))} min offline` : 'online time'} />
          <Metric label="Order rejection" value={a?.orderRejectionPct != null ? `${a.orderRejectionPct.toFixed(1)}%` : '—'} sub={a?.ordersMarkedReadyPct != null ? `${a.ordersMarkedReadyPct.toFixed(0)}% marked ready` : 'of orders'} />
        </div>
      </section>

      {/* ── Conversion funnel ── */}
      <section className="card insights-block" aria-label="Conversion funnel">
        <header className="insights-block__head">
          <h3>Conversion funnel</h3>
          <p className="muted">
            How many who saw the store went on to order
            {conv != null ? ` · ${conv.toFixed(1)}% converted` : ''}.
          </p>
        </header>
        {a && (a.impressions != null || a.menuVisits != null || a.conversions != null) ? (
          <div className="insights-funnel">
            <FunnelStep label="Impressions" value={a.impressions} />
            <FunnelStep label="Menu visits" value={a.menuVisits} prev={a.impressions} />
            <FunnelStep label="Carts started" value={a.cartsStarted} prev={a.menuVisits ?? a.impressions} />
            <FunnelStep label="Orders" value={a.conversions} prev={a.cartsStarted ?? a.menuVisits ?? a.impressions} />
          </div>
        ) : (
          <p className="muted">No funnel data recorded yet for {win.label}.</p>
        )}
      </section>

      {/* ── New vs returning ── */}
      <section className="card insights-block" aria-label="New vs returning customers">
        <header className="insights-block__head">
          <h3>New vs returning</h3>
          <p className="muted">
            {splitBody?.basis === 'deal'
              ? 'A floor from new-customer-deal orders (the only signal platforms report).'
              : 'This platform does not report a new-vs-returning split.'}
          </p>
        </header>
        {splitBody ? (
          <div className="insights-stats">
            <Metric label="Total orders" value={num(splitBody.orders)} sub={win.label} />
            <Metric label="New-customer orders" value={splitBody.basis === 'deal' ? num(splitBody.newCustomerOrders ?? 0) : '—'} sub={splitBody.basis === 'deal' ? 'via new-customer deals' : 'not reported'} />
            <Metric label="Returning orders" value={splitBody.basis === 'deal' ? num(splitBody.returningOrders ?? Math.max(0, splitBody.orders - (splitBody.newCustomerOrders ?? 0))) : num(splitBody.orders)} sub={splitBody.basis === 'deal' ? 'the rest' : 'all orders'} />
          </div>
        ) : (
          <p className="muted">{split.isLoading ? 'Loading…' : 'No orders in this period.'}</p>
        )}
      </section>

      {/* ── Deal incrementality ── */}
      <section className="card insights-block" aria-label="Deal incrementality">
        <header className="insights-block__head">
          <h3>Did my deals bring extra orders?</h3>
          <p className="muted">
            A best-effort ESTIMATE — extra orders a deal&apos;s days ran above a no-deal baseline. Not
            measured by the platform.
          </p>
        </header>
        {deals.length > 0 ? (
          <table className="data-table insights-deals" aria-label="Per-deal incrementality">
            <thead>
              <tr>
                <th>Deal</th>
                <th className="num">Extra orders (est.)</th>
                <th className="num">Orders/day with</th>
                <th className="num">Orders/day without</th>
                <th>Confidence</th>
              </tr>
            </thead>
            <tbody>
              {deals.map((d) => (
                <tr key={d.campaignName}>
                  <td>{d.campaignName}</td>
                  <td className="num">{d.estimatedExtraOrders.toFixed(1)}</td>
                  <td className="num">{d.ratePerDealDay.toFixed(1)}</td>
                  <td className="num">{d.baselineRatePerDay.toFixed(1)}</td>
                  <td>
                    <span className={`badge badge--${d.confidence === 'high' ? 'ok' : d.confidence === 'low' ? 'warn' : 'muted'}`}>
                      {d.confidence}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <p className="muted">{incr.isLoading ? 'Loading…' : 'No deals with enough data to estimate this month.'}</p>
        )}
      </section>

      {/* ── Opening hours ── */}
      {openingHours.length > 0 && (
        <section className="card insights-block" aria-label="Opening hours">
          <header className="insights-block__head">
            <h3>Opening hours</h3>
          </header>
          <ul className="insights-hours">
            {WEEKDAYS.map((label, wd) => (
              <li key={label} className="insights-hours__row">
                <span className="insights-hours__day">{label}</span>
                <span className="insights-hours__win num">{openingLine(openingHours.find((d) => d.weekday === wd))}</span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* ── Owner entry (what Clarity can't pull) ── */}
      {store.onboardingComplete && <StoreAnalyticsEntry store={store} />}

      {/* ── MyThings payouts & costs ── */}
      {store.platformKey === 'mythings' && store.onboardingComplete && (
        <MythingsManualEntries store={store} />
      )}
    </div>
  );
}

function Metric({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="stat">
      <span className="stat__label">{label}</span>
      <span className="stat__value num">{value}</span>
      <span className="stat__sub">{sub}</span>
    </div>
  );
}

/** One funnel step: the count + the drop-off from the previous step when both are known. */
function FunnelStep({ label, value, prev }: { label: string; value: number | null; prev?: number | null }) {
  const rate = value != null && prev != null && prev > 0 ? (value / prev) * 100 : null;
  return (
    <div className="insights-funnel__step">
      <span className="insights-funnel__label">{label}</span>
      <span className="insights-funnel__val num">{value != null ? num(value) : '—'}</span>
      {rate != null && <span className="insights-funnel__rate">{rate.toFixed(1)}% of previous</span>}
    </div>
  );
}
